import React, {ReactNode} from 'react';

import {translateAcceptanceContentType} from 'app/acceptance/types';
import {LazyTooltip} from 'app/components/general/LazyTooltip';
import {ApproveButton} from 'app/procedures/components/page-headers/RequestOrApproveButton/ApproveButton';
import {gt} from 'app/utils/i18n';

import {AcceptanceStatusButton} from './AcceptanceStatusButton';

import type {
  AcceptanceSerializedType,
  AcceptanceStatusSerializedType,
} from 'sweet-types';

type Props = {
  acceptance: AcceptanceSerializedType;
  documentId: number;
  documentHtmlURL: string;
  status: AcceptanceStatusSerializedType;
  versionURL: string;
  versionApprovedAt: string | null;
  nextReviewDate: string | null;
};

export const AcceptSidebarActions = ({
  status,
  versionApprovedAt,
  acceptance,
  documentId,
  versionURL,
  documentHtmlURL,
  nextReviewDate,
}: Props) => {
  if (versionApprovedAt != null) {
    return null;
  }

  const contentType = translateAcceptanceContentType(acceptance.content_type);

  const statusActions = [
    {
      targetStatus: true,
      icon: 'fa-thumbs-up',
      className: 'btn-sm btn-outline-primary btn-block',
      idleLabel: gt('Confirm'),
      activeLabel: gt('You confirmed'),
      busyLabel: gt('Confirming...'),
      helpContent: gt(
        'Confirming will indicate your support for this draft being approved.'
      ),
    },
    {
      targetStatus: false,
      icon: 'fa-thumbs-down',
      className: 'btn-sm btn-outline-danger btn-block',
      idleLabel: gt('Reject'),
      activeLabel: gt('You rejected'),
      busyLabel: gt('Rejecting...'),
      helpContent: gt(
        'Rejecting will indicate you do not support this draft being approved.'
      ),
    },
  ];

  return (
    <div className="py-3">
      <div className="small mb-1 px-3">
        {gt(
          'You can confirm your support for this draft or immediately approve it.'
        )}
      </div>

      <div className="px-4 mt-3">
        {statusActions.map((action) => (
          <ActionButtonLine
            key={String(action.targetStatus)}
            helpContent={action.helpContent}
          >
            <AcceptanceStatusButton
              className={action.className}
              contentType={contentType}
              documentId={documentId}
              acceptancestatus={status}
              targetStatus={action.targetStatus}
              icon={action.icon}
              idleLabel={action.idleLabel}
              activeLabel={action.activeLabel}
              busyLabel={action.busyLabel}
            />
          </ActionButtonLine>
        ))}
        <ActionButtonLine
          helpContent={gt(
            'Approve this draft and make it the new live version'
          )}
        >
          <ApproveButton
            groupClass="btn-block"
            className="btn btn-outline-primary btn-sm"
            contentType={contentType}
            documentId={documentId}
            documentHtmlURL={documentHtmlURL}
            versionURL={versionURL}
            disabled={versionApprovedAt != null}
            versionApprovedAt={versionApprovedAt}
            managers={[]}
            nextReviewDate={nextReviewDate}
            color="outline-primary"
            size="sm"
          />
        </ActionButtonLine>
      </div>
    </div>
  );
};

type ActionButtonLineProps = {
  children: ReactNode;
  helpContent: ReactNode;
};

function ActionButtonLine({children, helpContent}: ActionButtonLineProps) {
  return (
    <div className="d-flex align-items-center mb-3">
      <div className="flex-grow-1">{children}</div>
      <div className="pl-2 small text-muted">
        <LazyTooltip tooltip={helpContent}>
          <i className="ml-1 fa fa-question-circle" />
        </LazyTooltip>
      </div>
    </div>
  );
}
